import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button } from "@/components/ui/button"
import { Tables } from '@/../database.types'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { TrendingUp, Clock } from 'lucide-react'
import { supabase } from '@/lib/supabase'
import { useToast } from "@/components/ui/use-toast"
import convergenceBackgroundImage from '@/assets/images/convergence_background.png'

type Convergence = Tables<'convergences'>

interface ConvergenceCardProps {
  convergence: Convergence
}

export function ConvergenceCard({ convergence }: ConvergenceCardProps) {
  const navigate = useNavigate()
  const { toast } = useToast()
  const [statusLoading, setStatusLoading] = useState(false)
  const [currentStatus, setCurrentStatus] = useState(convergence.status)
  
  // Valid status values from database enum
  const validStatuses: Tables<'convergences'>['status'][] = ['Used', 'Unused', 'Unranked', 'Archived']
  
  const handleStatusChange = async (newStatus: Tables<'convergences'>['status']) => {
    if (!convergence || statusLoading) return;
    
    try {
      setStatusLoading(true);
      
      const { error } = await supabase
        .from('convergences')
        .update({ status: newStatus })
        .eq('id', convergence.id);

      if (error) throw error;

      // Update local state
      setCurrentStatus(newStatus);
      
      toast({
        title: "Status Updated",
        description: `Convergence status changed to ${newStatus}`,
      });
    } catch (error) {
      console.error('Error updating convergence status:', error);
      toast({
        title: "Error",
        description: "Failed to update convergence status",
        variant: "destructive",
      });
    } finally {
      setStatusLoading(false);
    }
  };
  
  // Check if convergence is expired
  const now = new Date()
  const expirationDate = convergence.expiration_date ? new Date(convergence.expiration_date) : null
  const isExpired = expirationDate && expirationDate <= now
  const daysUntilExpiration = expirationDate 
    ? Math.ceil((expirationDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24))
    : 0
  
  // Format expiration date
  const formatExpirationDate = () => {
    if (!expirationDate) return null
    
    if (isExpired) {
      const daysExpired = Math.abs(daysUntilExpiration)
      return `Expired ${daysExpired === 1 ? '1 day' : `${daysExpired} days`} ago`
    } else {
      if (daysUntilExpiration === 0) return 'Expires today'
      if (daysUntilExpiration === 1) return 'Expires tomorrow'
      return `Expires in ${daysUntilExpiration} days`
    }
  }

  return (
    <div className="group relative bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-lg transition-all duration-300 overflow-hidden flex flex-col h-full">
      {/* Hero Image Section - Always show with fallback */}
      <div 
        className="relative h-52 overflow-hidden bg-gray-100 cursor-pointer"
        onClick={() => navigate(`/convergences/${convergence.id}`)}
      >
        <img
          src={(convergence as any).image_url || convergenceBackgroundImage}
          alt={convergence.name}
          className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
          onError={(e) => {
            // Fallback to default background if image fails to load
            e.currentTarget.src = convergenceBackgroundImage;
          }}
        />
          <div className="absolute top-4 left-4" onClick={(e) => e.stopPropagation()}>
            <Select
              value={currentStatus}
              onValueChange={handleStatusChange}
              disabled={statusLoading}
            >
              <SelectTrigger 
                className={`h-auto w-auto border-0 font-medium text-xs px-2.5 py-1 ${
                  currentStatus === 'Used' 
                    ? 'bg-green-500 text-white hover:bg-green-600' 
                    : currentStatus === 'Archived'
                    ? 'bg-gray-500 text-white hover:bg-gray-600'
                    : 'bg-blue-500 text-white hover:bg-blue-600'
                }`}
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {validStatuses.map((status) => (
                  <SelectItem key={status} value={status} className="text-xs">
                    {status}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {expirationDate && (
            <div className="absolute top-4 right-4">
              <div className={`flex items-center gap-1 px-2 py-1 rounded-md bg-black/50 backdrop-blur-sm text-xs ${
                isExpired ? 'text-red-400' : daysUntilExpiration <= 3 ? 'text-orange-400' : 'text-white'
              }`}>
                <Clock className="h-3 w-3" />
                <span className="font-medium">{formatExpirationDate()}</span>
              </div>
            </div>
          )}
        </div>
      
      <div className="flex-1 p-5 space-y-4 flex flex-col">

        {/* Title and Description */}
        <div className="flex-1">
          <h3 
            className="font-bold text-lg text-gray-900 mb-2 line-clamp-2 group-hover:text-blue-600 transition-colors cursor-pointer"
            onClick={() => navigate(`/convergences/${convergence.id}`)}
          >
            {convergence.name}
          </h3>
          <p className="text-sm text-gray-600 line-clamp-2">
            {convergence.trend_description || convergence.convergence || convergence.trend}
          </p>
        </div>

        {/* Power Score */}
        <div className="py-3 border-t border-gray-100">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm font-medium text-gray-700">Power Score</span>
            <div className="flex items-center gap-1">
              <TrendingUp className="h-4 w-4 text-blue-600" />
              <span className="text-2xl font-bold text-blue-600">
                {(convergence.power_score || 0).toFixed(1)}
              </span>
            </div>
          </div>
          
          {/* Score Grid */}
          <div className="grid grid-cols-2 gap-3">
            <div className="text-center">
              <div className="text-lg font-bold text-purple-600">
                {(convergence.virality_score || 0).toFixed(1)}
              </div>
              <div className="text-xs text-gray-500">Virality</div>
            </div>
            <div className="text-center">
              <div className="text-lg font-bold text-blue-600">
                {(convergence.relevance_score || 0).toFixed(1)}
              </div>
              <div className="text-xs text-gray-500">Relevance</div>
            </div>
            <div className="text-center">
              <div className="text-lg font-bold text-green-600">
                {(convergence.interest_to_audience_score || 0).toFixed(1)}
              </div>
              <div className="text-xs text-gray-500">Interest</div>
            </div>
            <div className="text-center">
              <div className="text-lg font-bold text-orange-600">
                {(convergence.crossover_potential_score || 0).toFixed(1)}
              </div>
              <div className="text-xs text-gray-500">Crossover</div>
            </div>
          </div>
        </div>

        {/* Footer with Date */}
        <div className="text-xs text-gray-400 pt-3 border-t border-gray-100">
          Created {new Date(convergence.created_at).toLocaleDateString('en-US', { 
            month: 'short', 
            day: 'numeric', 
            year: 'numeric' 
          })}
        </div>

        {/* Action button */}
        <div className="pt-2">
          <Button 
            size="sm" 
            className="w-full bg-blue-600 hover:bg-blue-700 text-white transition-colors" 
            onClick={() => navigate(`/convergences/${convergence.id}`)}
          >
            View
          </Button>
        </div>
      </div>
    </div>
  )
}